import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { WandSparkles } from 'lucide-react';
import { AI_ENGINEERING_MODULE_OPTIONS, AI_ENGINEERING_RISK_OPTIONS, getAIEngineeringRiskLabel, getAIEngineeringRiskTone } from '@/lib/aiEngineeringLabels';

export const AIEngineeringTaskComposer = ({ form, onChange, onSubmit, submitting, disabled = false, helperText = '' }) => {
  const isDisabled = submitting || disabled || !form.title.trim() || !form.request_text.trim();

  return (
    <Card className="border-slate-200 shadow-sm" data-testid="ai-engineering-composer-card">
      <CardHeader className="space-y-2 pb-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base font-semibold text-slate-900">Nova task técnica</CardTitle>
            <CardDescription className="text-xs text-slate-600">Pedido em linguagem natural, pronto para análise mockada e aprovação humana.</CardDescription>
          </div>
          <Badge variant="outline" className={`rounded-sm border ${getAIEngineeringRiskTone(form.risk_level)}`}>
            Risco {getAIEngineeringRiskLabel(form.risk_level)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="ai-engineering-title" className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Título</Label>
          <Input id="ai-engineering-title" value={form.title} onChange={(event) => onChange('title', event.target.value)} placeholder="Ex.: Melhorar resumo operacional do módulo de relatórios" disabled={disabled} data-testid="ai-engineering-title-input" />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ai-engineering-request" className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Pedido</Label>
          <Textarea id="ai-engineering-request" value={form.request_text} onChange={(event) => onChange('request_text', event.target.value)} placeholder="Descreva a alteração pedida, impacto esperado e contexto operacional." className="min-h-[132px] resize-none" disabled={disabled} data-testid="ai-engineering-request-textarea" />
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Módulo</Label>
            <Select value={form.module_target} onValueChange={(value) => onChange('module_target', value)} disabled={disabled}>
              <SelectTrigger data-testid="ai-engineering-module-select">
                <SelectValue placeholder="Seleccionar módulo" />
              </SelectTrigger>
              <SelectContent>
                {AI_ENGINEERING_MODULE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Risco</Label>
            <Select value={form.risk_level} onValueChange={(value) => onChange('risk_level', value)} disabled={disabled}>
              <SelectTrigger data-testid="ai-engineering-risk-select">
                <SelectValue placeholder="Seleccionar risco" />
              </SelectTrigger>
              <SelectContent>
                {AI_ENGINEERING_RISK_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {helperText ? <p className="text-xs text-slate-500" data-testid="ai-engineering-composer-helper-text">{helperText}</p> : null}

        <Button className="h-9 w-full rounded-sm bg-slate-900 text-xs font-semibold hover:bg-slate-800" onClick={onSubmit} disabled={isDisabled} data-testid="ai-engineering-create-button">
          <WandSparkles className="mr-2 h-4 w-4" />
          {submitting ? 'A criar task...' : 'Criar task AI Engineering'}
        </Button>
      </CardContent>
    </Card>
  );
};